import type {
  NodeExecutionDirective,
  PipelineGraphSkeleton,
  PipelineNodeRuntimeStatus,
} from "@/types/aicheck";
import { collectDescendantNodeIds } from "./admin-rerun-planner";

export function normalizeNodeExecutionDirectives(
  directives: NodeExecutionDirective[] | undefined | null,
): NodeExecutionDirective[] {
  if (!directives || directives.length === 0) return [];
  const next = new Map<string, NodeExecutionDirective>();
  for (const directive of directives) {
    if (
      !directive ||
      typeof directive.rootNodeId !== "string" ||
      !directive.rootNodeId
    ) {
      continue;
    }
    if (directive.mode !== "blocked_subtree") continue;
    if (directive.state !== "scheduled" && directive.state !== "stale") {
      continue;
    }
    next.set(directive.rootNodeId, structuredClone(directive));
  }
  return [...next.values()];
}

export function getScheduledNodeExecutionDirectives(
  directives: NodeExecutionDirective[] | undefined | null,
): NodeExecutionDirective[] {
  return normalizeNodeExecutionDirectives(directives).filter(
    (directive) => directive.state === "scheduled",
  );
}

export function findNodeExecutionDirective(
  directives: NodeExecutionDirective[] | undefined | null,
  rootNodeId: string,
): NodeExecutionDirective | null {
  return (
    normalizeNodeExecutionDirectives(directives).find(
      (directive) => directive.rootNodeId === rootNodeId,
    ) ?? null
  );
}

export function buildBlockedNodeIdSet(
  graphSkeleton: PipelineGraphSkeleton | undefined | null,
  directives: NodeExecutionDirective[] | undefined | null,
): Set<string> {
  const blocked = new Set<string>();
  if (!graphSkeleton) return blocked;
  for (const directive of getScheduledNodeExecutionDirectives(directives)) {
    for (const nodeId of collectDescendantNodeIds(
      graphSkeleton,
      directive.rootNodeId,
    )) {
      blocked.add(nodeId);
    }
  }
  return blocked;
}

export function isTerminalNodeRuntimeStatus(
  status: PipelineNodeRuntimeStatus | undefined,
): boolean {
  return (
    status === "completed" ||
    status === "failed" ||
    status === "skipped" ||
    status === "aborted" ||
    status === "blocked"
  );
}

export function isPendingNodeRuntimeStatus(
  status: PipelineNodeRuntimeStatus | undefined,
): boolean {
  return status === "planned" || status === "pending" || status == null;
}

/**
 * サーバから受け取ったノード状態のうち、**適用してよいものだけ**を返す。
 *
 * **空・不在・型違いは「状態」ではない。** サーバは中断直後などに
 * レジストリを捨ててから応答することがあり、その場合ノード状態は `{}` で
 * 届く。これを現在値として適用すると、直前まで分かっていた状態が
 * すべて消え、画面は残った断片を全体像として描く（灰色が残る・
 * 実行中のまま点滅する、という形で繰り返し表に出た）。
 *
 * 「知らない」と「何も無い」を区別できないなら、**適用しない**のが正しい。
 * 意図して消したいときは `clearNodeExecutionState()` を使う。
 */
export function authoritativeNodeStates(
  raw: unknown,
): Record<string, PipelineNodeRuntimeStatus> | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const states = raw as Record<string, PipelineNodeRuntimeStatus>;
  return Object.keys(states).length > 0 ? states : undefined;
}
